import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { AUTH_CALLBACK_URL, PRODUCTION_ORIGIN } from "@/constants";
import { Zap } from "lucide-react";
import { PASSWORD_REQUIREMENTS, validatePassword } from "@/lib/password";

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden>
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

/** Supabase limits: 2 emails/hour total; 60s per-user before resend. */
const RESEND_COOLDOWN_SEC = 60;
type AuthMode = "login" | "signup" | "forgot" | "recovery";
const LOCAL_HOSTNAMES = new Set(["localhost", "127.0.0.1"]);

function getAuthMode(value: string | null): AuthMode {
  return value === "signup" || value === "forgot" || value === "recovery" ? value : "login";
}

function isRateLimitError(err: { message?: string } | null): boolean {
  if (!err?.message) return false;
  const m = err.message.toLowerCase();
  return m.includes("rate limit") || m.includes("429") || m.includes("too many requests") || m.includes("email rate limit");
}

function getRateLimitMessageForSignup(): string {
  return "We're temporarily unable to send confirmation emails. Please try again in about an hour. Already received a confirmation link? Sign in with your password below.";
}

function getRateLimitMessageForResend(): string {
  return "We can only send a limited number of emails per hour. Please wait about an hour before requesting another. If you've already confirmed your email, sign in with your password below.";
}

export default function AuthPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const [rateLimitOnSignup, setRateLimitOnSignup] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldownUntil, setResendCooldownUntil] = useState<number>(0);
  const [resendCooldownLeft, setResendCooldownLeft] = useState(0);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const { toast } = useToast();
  const mode = getAuthMode(searchParams.get("mode"));
  const isLogin = mode === "login";
  const isSignup = mode === "signup";
  const isForgotPassword = mode === "forgot";
  const isRecoveryMode = mode === "recovery";
  const isLocalhost = typeof window !== "undefined" && LOCAL_HOSTNAMES.has(window.location.hostname);
  const passwordRecoveryRedirectUrl = isLocalhost
    ? `${window.location.origin}/auth/confirm?type=recovery`
    : `${PRODUCTION_ORIGIN}/auth/confirm?type=recovery`;

  const getPasswordStrength = (value: string) => {
    if (!value) return { score: 0, label: "", colorClass: "", widthClass: "w-0" };

    let score = 0;
    if (value.length >= 8) score++;
    if (/[A-Z]/.test(value)) score++;
    if (/[a-z]/.test(value)) score++;
    if (/[0-9]/.test(value)) score++;
    if (/[^A-Za-z0-9]/.test(value)) score++;

    if (score <= 2) {
      return { score, label: "Weak", colorClass: "bg-destructive", widthClass: "w-1/3" };
    }
    if (score === 3 || score === 4) {
      return { score, label: "Okay", colorClass: "bg-amber-500", widthClass: "w-2/3" };
    }
    return { score, label: "Strong", colorClass: "bg-emerald-500", widthClass: "w-full" };
  };

  const switchMode = (nextMode: AuthMode) => {
    const params = new URLSearchParams(searchParams);
    if (nextMode === "login") {
      params.delete("mode");
    } else {
      params.set("mode", nextMode);
    }
    params.delete("error");
    params.delete("message");
    setPassword("");
    setConfirmPassword("");
    setPasswordError(null);
    setAuthError(null);
    setRateLimitOnSignup(false);
    setResetSent(false);
    setSearchParams(params, { replace: true });
  };

  useEffect(() => {
    if (resendCooldownUntil <= 0) {
      setResendCooldownLeft(0);
      return;
    }
    const tick = () => {
      const left = Math.max(0, Math.ceil((resendCooldownUntil - Date.now()) / 1000));
      setResendCooldownLeft(left);
      if (left <= 0) setResendCooldownUntil(0);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [resendCooldownUntil]);

  useEffect(() => {
    if (user && !isRecoveryMode) {
      navigate("/", { replace: true });
      return;
    }
  }, [user, navigate, isRecoveryMode]);

  useEffect(() => {
    const error = searchParams.get("error");
    const message = searchParams.get("message");
    if (error === "auth_callback_failed") {
      const desc = message
        ? `Authentication error: ${message}. Try again or sign in with email/password.`
        : "The sign-in link may have expired or been used (e.g. by your email provider). Try signing in with your password if you've already confirmed, or request a new confirmation email below.";
      setAuthError(desc);
      toast({ title: "Sign-in failed", description: desc, variant: "destructive" });
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, toast]);

  const handleResendConfirmation = async () => {
    if (!email?.trim()) {
      toast({ title: "Enter your email first", description: "Type the email you used to sign up, then click Resend.", variant: "destructive" });
      return;
    }
    if (resendCooldownLeft > 0) return;
    setResendLoading(true);
    const { error } = await supabase.auth.resend({ type: "signup", email: email.trim() });
    setResendLoading(false);
    if (error) {
      if (isRateLimitError(error)) {
        toast({ title: "Email limit reached", description: getRateLimitMessageForResend(), variant: "destructive" });
        setResendCooldownUntil(Date.now() + RESEND_COOLDOWN_SEC * 1000);
      } else {
        toast({ title: "Could not resend", description: error.message, variant: "destructive" });
      }
    } else {
      setAuthError(null);
      toast({ title: "Check your email", description: "We sent a new confirmation link. Click it to verify your account.", variant: "default" });
      setResendCooldownUntil(Date.now() + RESEND_COOLDOWN_SEC * 1000);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      toast({ title: "Enter your email first", description: "Type your account email so we can send a reset link.", variant: "destructive" });
      return;
    }
    setResetLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: passwordRecoveryRedirectUrl,
    });
    setResetLoading(false);
    if (error) {
      toast({ title: "Could not send reset link", description: error.message, variant: "destructive" });
      return;
    }
    setResetSent(true);
    toast({
      title: "Reset link sent",
      description: "Check your email and open the link to choose a new password.",
    });
  };

  const handleGoogleSignIn = async () => {
    setOauthLoading(true);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: AUTH_CALLBACK_URL, // window.location.origin + /auth/callback (works on app.reviewboost24.com)
      },
    });
    if (error) {
      toast({ title: "Google sign-in failed", description: error.message, variant: "destructive" });
      setOauthLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    if (isSignup || isRecoveryMode) {
      const pwdError = validatePassword(password);
      if (pwdError) {
        setPasswordError(pwdError);
        setLoading(false);
        return;
      }
      if (isRecoveryMode && password !== confirmPassword) {
        setPasswordError("Passwords do not match");
        setLoading(false);
        return;
      }
    }
    setPasswordError(null);

    if (isRecoveryMode) {
      if (!user) {
        setLoading(false);
        toast({
          title: "Recovery session expired",
          description: "Please request another reset link to set a new password.",
          variant: "destructive",
        });
        switchMode("forgot");
        return;
      }
      const { error } = await supabase.auth.updateUser({ password });
      if (error) {
        toast({ title: "Password update failed", description: error.message, variant: "destructive" });
        setLoading(false);
        return;
      }
      toast({ title: "Password updated", description: "Your new password has been saved." });
      setLoading(false);
      navigate("/", { replace: true });
      return;
    }

    if (isLogin) {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        const msg =
          error.message === "Invalid login credentials" || error.message?.toLowerCase().includes("invalid")
            ? "Email or password is incorrect. Try again or use Sign Up if you don't have an account yet."
            : error.message;
        toast({ title: "Sign in failed", description: msg, variant: "destructive" });
      }
    } else {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: AUTH_CALLBACK_URL, // window.location.origin + /auth/callback
          data: { full_name: fullName },
        },
      });
      if (error) {
        let msg =
          error.message === "Invalid login credentials" || error.message?.toLowerCase().includes("invalid")
            ? "Sign up failed. Please use a different email or sign in if you already have an account."
            : error.message || "Sign up failed. Try again or use a different email.";
        if (isRateLimitError(error)) {
          msg = getRateLimitMessageForSignup();
          setRateLimitOnSignup(true);
        }
        toast({ title: "Sign up failed", description: msg, variant: "destructive" });
      } else {
        toast({
          title: "Check your email",
          description: "We sent you a confirmation link. Click it to verify your account, then you'll be signed in.",
          variant: "default",
        });
      }
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-foreground p-4">
      <Card className="w-full max-w-md border-primary/20 bg-card shadow-2xl shadow-black/40">
        <CardHeader className={`space-y-3 ${isRecoveryMode ? "pb-4 text-center" : "text-center"}`}>
          <div className="mx-auto flex items-center gap-2">
            <Zap className="h-8 w-8 text-primary" />
            <span className="text-2xl font-display font-bold text-foreground">ReviewBoost Pro Manager</span>
          </div>
          <CardTitle className={isRecoveryMode ? "text-3xl font-bold" : "text-xl font-bold"}>
            {isRecoveryMode ? "New Password" : isForgotPassword ? "Forgot Password?" : isLogin ? "Welcome back" : "Welcome to ReviewBoost"}
          </CardTitle>
          <CardDescription className={isRecoveryMode ? "mx-auto max-w-sm text-sm leading-6" : undefined}>
            {isRecoveryMode
              ? "Please choose a new password. Use upper and lower-case letters, numbers, and special characters for a strong password."
              : isForgotPassword
                ? "Enter your account email and we will send you a secure link to reset your password."
                : isLogin
                  ? "Your command centre for inventory, sales and growth. Pick up where you left off."
                  : "Run your business from one place. Inventory, sales and reports that actually help you grow."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {rateLimitOnSignup && isSignup && (
            <div className="mb-4 rounded-lg border border-amber-500/50 bg-amber-500/10 px-4 py-3 text-sm">
              <p className="font-medium text-amber-700 dark:text-amber-400">Email limit reached</p>
              <p className="mt-1 text-amber-800 dark:text-amber-300">{getRateLimitMessageForSignup()}</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-3 w-full border-amber-600 text-amber-700 hover:bg-amber-500/20 dark:text-amber-400 dark:hover:bg-amber-500/20"
                onClick={() => {
                  switchMode("login");
                }}
              >
                Sign in instead
              </Button>
            </div>
          )}
          {authError && (
            <div className="mb-4 rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              <p className="font-medium">Sign-in failed</p>
              <p className="mt-1">{authError}</p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <button type="button" className="text-primary hover:underline" onClick={() => setAuthError(null)}>
                  Dismiss
                </button>
                <span className="text-muted-foreground">·</span>
                <button
                  type="button"
                  className="text-primary hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleResendConfirmation}
                  disabled={resendLoading || resendCooldownLeft > 0}
                >
                  {resendLoading ? "Sending…" : resendCooldownLeft > 0 ? `Resend in ${resendCooldownLeft}s` : "Resend confirmation email"}
                </button>
              </div>
            </div>
          )}
          {isForgotPassword ? (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="reset-email">Email</Label>
                <Input
                  id="reset-email"
                  name="reset-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                />
              </div>
              {resetSent && (
                <div className="rounded-lg border border-primary/30 bg-primary/10 px-4 py-3 text-sm text-foreground">
                  Check your email for the password reset link. If you do not see it, check your spam folder and try again.
                </div>
              )}
              <Button type="submit" className="w-full font-semibold" disabled={resetLoading}>
                {resetLoading ? "Sending reset link..." : "Send Reset Link"}
              </Button>
              <button type="button" onClick={() => switchMode("login")} className="w-full text-sm text-primary hover:underline">
                Back to sign in
              </button>
            </form>
          ) : (
            <>
              {!isRecoveryMode && (
                <div className="space-y-3 mb-6">
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full font-medium bg-white text-black border border-border hover:bg-white/90 hover:text-black"
                    disabled={loading || oauthLoading}
                    onClick={handleGoogleSignIn}
                  >
                    <GoogleIcon className="mr-2 h-4 w-4" />
                    {oauthLoading ? "Redirecting…" : "Continue with Google"}
                  </Button>
                  <div className="relative">
                    <span className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-border" />
                    </span>
                    <span className="relative flex justify-center text-[11px] uppercase text-muted-foreground px-3 bg-card">
                      Or continue with email
                    </span>
                  </div>
                </div>
              )}
              <form data-testid={isLogin ? "login-form" : isRecoveryMode ? "recovery-form" : "signup-form"} onSubmit={handleSubmit} className="space-y-4">
                {isSignup && (
                  <div className="space-y-2">
                    <Label htmlFor="fullName">Full Name</Label>
                    <Input id="fullName" name="fullName" autoComplete="name" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="John Doe" required />
                  </div>
                )}
                {!isRecoveryMode && (
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input data-testid={isLogin ? "login-email-input" : "signup-email-input"} id="email" name="email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required />
                  </div>
                )}
                {isRecoveryMode ? (
                  <div className="space-y-3 rounded-2xl bg-muted/35 p-4">
                    <Input
                      data-testid="recovery-password-input"
                      id="password"
                      name="password"
                      type="password"
                      autoComplete="new-password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        setPasswordError(null);
                      }}
                      placeholder="New Password"
                      required
                      minLength={8}
                      className="h-14 rounded-xl border-0 bg-background px-4 text-base shadow-sm"
                    />
                    <Input
                      data-testid="recovery-confirm-password-input"
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      autoComplete="new-password"
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        setPasswordError(null);
                      }}
                      placeholder="Confirm Password"
                      required
                      minLength={8}
                      className="h-14 rounded-xl border-0 bg-background px-4 text-base shadow-sm"
                    />
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <Input
                      data-testid={isLogin ? "login-password-input" : "signup-password-input"}
                      id="password"
                      name="password"
                      type="password"
                      autoComplete={isLogin ? "current-password" : "new-password"}
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        setPasswordError(null);
                      }}
                      placeholder="••••••••"
                      required
                      minLength={8}
                    />
                    {(isSignup || isRecoveryMode) && password && (
                      <div className="mt-1 flex items-center justify-between gap-3" data-testid="password-strength">
                        <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${getPasswordStrength(password).colorClass} ${getPasswordStrength(password).widthClass}`}
                          />
                        </div>
                        <span className="ml-2 text-xs text-muted-foreground">
                          {getPasswordStrength(password).label}
                        </span>
                      </div>
                    )}
                  </div>
                )}
                {passwordError && <p className="text-sm text-destructive">{passwordError}</p>}
                {isLogin && (
                  <button type="button" onClick={() => switchMode("forgot")} className="text-sm text-primary hover:underline">
                    Forgot password?
                  </button>
                )}
                <Button data-testid={isLogin ? "login-submit-btn" : isRecoveryMode ? "recovery-submit-btn" : "signup-submit-btn"} type="submit" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold" disabled={loading}>
                  {loading ? "Please wait..." : isRecoveryMode ? "Update Password" : isLogin ? "Sign In" : "Sign Up"}
                </Button>
              </form>
              {!isRecoveryMode && (
                <div className="mt-4 text-center space-y-2">
                  <button
                    type="button"
                    onClick={() => switchMode(isLogin ? "signup" : "login")}
                    className="text-sm text-primary hover:underline"
                  >
                    {isLogin ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
                  </button>
                </div>
              )}
              {isRecoveryMode && (
                <div className="mt-4 text-center">
                  <button type="button" onClick={() => switchMode("forgot")} className="text-sm text-primary hover:underline">
                    Need a fresh reset link?
                  </button>
                </div>
              )}
            </>
          )}
          <footer className="mt-6 pt-4 border-t border-border flex justify-center gap-4 text-xs text-muted-foreground">
            <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-foreground transition-colors">Terms of Service</Link>
          </footer>
        </CardContent>
      </Card>
    </div>
  );
}
